import {DocumentFolder} from "../model/documentFolder";
import {Document} from "../model/document";

export default class DocumentFolderUtils {


  /**
   * Recursively calculates the maximum depth of subfolders within all docuemnts of a property.
   *
   * @param documents Array of all documents of a property which contain folders to traverse.
   * @param currentDepth The current depth in the folder structure (default is 0).
   * @returns The maximum depth of subfolders within the Property structure.
   */
  public static getMaxSubfolderDepthInPropertyDocuments(documents: Document[], currentDepth: number = 0): number {
    let maxDepth = currentDepth;
    for (const document of documents) {
      const depth = DocumentFolderUtils.getMaxSubfolderDepthInDocument(document, currentDepth);
      if (depth > maxDepth) {
        maxDepth = depth;
      }
    }
    return maxDepth;
  }

  /**
   * Recursively calculates the maximum depth of subfolders within a Document structure.
   *
   * @param document The Document object containing folders to traverse.
   * @param currentDepth The current depth in the folder structure (default is 0).
   * @returns The maximum depth of subfolders within the Document structure.
   */
  public static getMaxSubfolderDepthInDocument(document: Document, currentDepth: number = 0): number {
    let maxDepth = currentDepth;
    for (const folder of document.folders) {
      const depth = DocumentFolderUtils.getMaxSubfolderDepth(folder, currentDepth);
      if (depth > maxDepth) {
        maxDepth = depth;
      }
    }
    return maxDepth;
  }

  /**
   * Recursively calculates the maximum depth of subfolders within a DocumentFolder structure.
   *
   * @param folder The root DocumentFolder to start the depth calculation from.
   * @param currentDepth The current depth in the folder structure (default is 0).
   * @returns The maximum depth of subfolders within the folder structure.
   */
  public static getMaxSubfolderDepth(folder: DocumentFolder, currentDepth: number = 0): number {
    if (!folder.subFolders || folder.subFolders.length === 0) {
      return currentDepth;
    } else {
      let maxDepth = currentDepth;
      for (const subFolder of folder.subFolders) {
        const depth = this.getMaxSubfolderDepth(subFolder, currentDepth + 1);
        if (depth > maxDepth) {
          maxDepth = depth;
        }
      }
      return maxDepth;
    }
  }

  /**
   * Recursively flattens a hierarchical structure of document folders into a flat array of document folders.
   *
   * @param folders An array of DocumentFolder objects representing the folders to flatten.
   * @returns A flat array of DocumentFolder objects containing all folders and subfolders.
   */
  public static flattenFolders(folders: DocumentFolder[]): DocumentFolder[] {
    // Views that do not request files and folders deliver null for them; they are loaded separately.
    return (folders ?? []).reduce((acc, folder) => {
      // Push the current folder to the accumulator array
      acc.push(folder);
      // If the current folder has subfolders, recursively flatten them and push to the accumulator array
      if (folder.subFolders && folder.subFolders.length > 0) {
        acc.push(...this.flattenFolders(folder.subFolders));
      }
      return acc;
    }, []);
  }

}
